import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSave,
    faSearch,
    faRefresh,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faBox,
    faTag,
    faBuilding,
    faBalanceScale,
    faDollarSign,
    faCalculator,
    faExclamationCircle,
    faHashtag,
    faPlus,
    faBarcode,
    faLayerGroup,
    faIndustry,
    faWeightHanging,
    faWarehouse,
    faFileExport,
    faChartLine,
    faPercent,
    faUser,
    faUserPlus,
    faFileInvoice,
    faTruck,
    faPhone,
    faMapMarkerAlt,
    faFileImage,
    faCloudUploadAlt,
    faBoxes,
    faShoppingCart,
    faCreditCard,
    faMoneyBillWave,
    faStore,
    faExchangeAlt,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance with the SAME pattern as Products
const createPurchasesListTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Purchases List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        // Main headers
        purchaseList: 'Purchase List',
        allPurchases: 'Shop Purchases',
        purchases: 'Purchases',

        // Search and actions
        searchPlaceholder: 'Search purchases in shop...',
        refresh: 'Refresh',
        loadingPurchases: 'Loading shop purchases...',
        noPurchasesFound: 'No purchases found in shop',

        // Modal texts
        purchaseDetails: 'Purchase Details',
        close: 'Close',

        // Table headers
        serialNo: 'S.No',
        id: 'ID',
        date: 'Date',
        billNo: 'Bill No',
        supplierName: 'Supplier',
        phone: 'Phone',
        address: 'Address',
        supplierid: 'Supplier ID',
        items: 'Items',
        grandTotal: 'Total',
        paid: 'Paid',
        balance: 'Balance',
        billFile: 'Bill File',
        actions: 'Actions',
        addedBy: 'Added By',
        shopInfo: 'Shop',

        // Action buttons
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',

        // Messages
        deleteConfirm: 'Are you sure you want to delete this purchase?',
        deletedSuccess: 'Purchase deleted successfully',
        deleteError: 'Error deleting purchase',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',
        noShopPermission: 'You are not assigned to any shop. Please contact administrator.',

        // Modal details
        purchaseId: 'Purchase ID',
        supplier: 'Supplier',
        billNumber: 'Bill Number',
        name: 'Name',
        notProvided: 'Not provided',
        itemCount: 'Items',
        totalAmount: 'Total Amount',
        paidAmount: 'Paid Amount',
        balanceAmount: 'Balance',
        paymentStatus: 'Payment Status',
        paid: 'Paid',
        unpaid: 'Unpaid',
        partiallyPaid: 'Partially Paid',

        // Pagination
        showing: 'Showing',
        of: 'of',
        purchases: 'purchases',
        previous: 'Previous',
        next: 'Next',

        // Filter texts
        filter: 'Filter',
        all: 'All',
        paidFilter: 'Paid',
        unpaidFilter: 'Unpaid',
        partiallyPaidFilter: 'Partially Paid',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        billNoFilter: 'Bill Number',
        supplierFilter: 'Supplier Name',
        phoneFilter: 'Phone',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'PDF',
        printList: 'Print List',
        exportData: 'Export Data',
        filterResults: 'Filter Results',
        clearAll: 'Clear All',
        addedByFilter: 'Added By',

        // Edit Modal - Add Purchase Form texts
        editPurchase: 'Edit Purchase',
        updatePurchase: 'Update Purchase',
        updating: 'Updating...',
        purchaseUpdated: 'Purchase updated successfully',
        updateError: 'Error updating purchase',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the purchase details below',

        // Validation messages
        dateRequired: 'Date is required',
        billNoRequired: 'Bill number is required',
        billNoExists: 'This bill number already exists in this shop',
        supplierRequired: 'Supplier is required',
        supplierNameRequired: 'Supplier name is required',
        grandTotalRequired: 'Grand total is required',
        paidRequired: 'Paid amount is required',
        invalidAmount: 'Please enter a valid amount',
        itemsRequired: 'At least one item is required',

        // Delete Confirmation Modal
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this purchase?',
        deleteWarning: 'This action cannot be undone. All associated items will also be deleted.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',

        // User-specific messages
        myRecords: 'Shop Purchases Only',
        loadingUserData: 'Loading shop purchase data...',
        noUserRecords: 'No purchases found in this shop',

        // Business Info
        businessReport: 'Business Report',
        generatedBy: 'Generated by',
        purchaseReport: 'Purchase Report',
        inventoryReport: 'Inventory Report',

        // Toast Messages
        success: 'Success',
        error: 'Error',
        warning: 'Warning',

        // Payment Status
        paymentStatus: 'Payment Status',
        totalPurchases: 'Total Purchases',
        totalPaid: 'Total Paid',
        totalBalance: 'Total Balance',
        totalAmount: 'Total Amount',

        // File upload
        viewBill: 'View Bill',
        noBill: 'No Bill',

        // Item columns
        itemProduct: 'Product',
        itemBatch: 'Batch',
        itemUnit: 'Unit',
        itemPrice: 'Price',
        itemQuantity: 'Qty',
        itemTotal: 'Total',
        itemExpiry: 'Expiry',

        // Additional
        editMode: 'Edit Mode',
        readonlyNotice: 'Some fields cannot be edited after creation',
        
        // Shop related
        currentShop: 'Current Shop',
        willShowFromShop: 'Showing purchases from shop',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        noShopPermission: 'You do not have permission to view purchases in this shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        active: 'Active',
        current: 'Current'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const purchasesListTranslator = createPurchasesListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div 
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const PurchaseList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [purchases, setPurchases] = useState([]);
    const [filteredPurchases, setFilteredPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [purchaseToDelete, setPurchaseToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    
    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Shop info state
    const [shopInfo, setShopInfo] = useState({
        shop_id: null,
        shop_name: '',
        shop_code: ''
    });

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Timer reference for cleanup
    const toastTimerRef = useRef(null);

    // Edit form state with validation errors
    const [editFormData, setEditFormData] = useState({
        date: '',
        bill_no: '',
        supplierid: '',
        supplier_name: '',
        phone: '',
        address: '',
        grand_total: '',
        paid: '',
        balance: '',
        items: []
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });
    const [businessInfoLoading, setBusinessInfoLoading] = useState(false);

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterType, setFilterType] = useState('all');
    const [filterBy, setFilterBy] = useState('all');
    const [billNoFilter, setBillNoFilter] = useState('');
    const [supplierFilter, setSupplierFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Calculate totals
    const totalPurchases = filteredPurchases.length;
    const totalGrandTotal = filteredPurchases.reduce((sum, purchase) => {
        return sum + (parseFloat(purchase.grand_total) || 0);
    }, 0);
    const totalPaid = filteredPurchases.reduce((sum, purchase) => {
        return sum + (parseFloat(purchase.paid) || 0);
    }, 0);
    const totalBalance = filteredPurchases.reduce((sum, purchase) => {
        return sum + (parseFloat(purchase.balance) || 0);
    }, 0);
    const paidCount = filteredPurchases.filter(purchase => {
        const balance = parseFloat(purchase.balance) || 0;
        return balance === 0;
    }).length;
    const unpaidCount = filteredPurchases.filter(purchase => {
        const paid = parseFloat(purchase.paid) || 0;
        const grandTotal = parseFloat(purchase.grand_total) || 0;
        return paid === 0;
    }).length;
    const partiallyPaidCount = filteredPurchases.filter(purchase => {
        const paid = parseFloat(purchase.paid) || 0;
        const grandTotal = parseFloat(purchase.grand_total) || 0;
        const balance = parseFloat(purchase.balance) || 0;
        return paid > 0 && balance > 0;
    }).length;

    const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPurchases = filteredPurchases.slice(startIndex, endIndex);

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme from login page
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();

        window.addEventListener('storage', handleThemeChange);

        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await purchasesListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();

        window.addEventListener('storage', handleLanguageChange);

        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction when language changes
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
            const tables = document.querySelectorAll('.customer-list-modern, .table-container-modern, .customer-table-modern');
            tables.forEach(table => {
                table.style.direction = 'rtl';
                table.style.textAlign = 'right';
            });
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
            const tables = document.querySelectorAll('.customer-list-modern, .table-container-modern, .customer-table-modern');
            tables.forEach(table => {
                table.style.direction = 'ltr';
                table.style.textAlign = 'left';
            });
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Load available shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };

        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    // Fetch purchases on mount and when currentShop changes
    useEffect(() => {
        if (user && user.username) {
            if (currentShop) {
                fetchPurchases();
                fetchBusinessInfo();
            } else {
                setLoading(false);
                showToast('warning', translations.noShopSelected || 'Please select a shop first');
            }
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user, currentShop]);

    // Apply filters when purchases, search term, or filter type changes
    useEffect(() => {
        applyFilters();
    }, [purchases, searchTerm, filterType, filterBy, billNoFilter, supplierFilter, phoneFilter, addedByFilter, startDate, endDate]);

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id, 
                shop.shop_name, 
                shop.shop_code
            );
            
            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
                setCurrentPage(1);
                resetFilters();
                // Fetch purchases after switching shop
                fetchPurchases();
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // Fetch business info from root_users
    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;

        setBusinessInfoLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            } else {
                console.log('Could not fetch business info:', data.message);
                setBusinessInfo({
                    businessname: user.business_name || '',
                    bussinesslogo: '',
                    businessaddress: '',
                    fullname: user.fullname || '',
                    email: user.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            setBusinessInfo({
                businessname: user.business_name || '',
                bussinesslogo: '',
                businessaddress: '',
                fullname: user.fullname || '',
                email: user.email || ''
            });
        } finally {
            setBusinessInfoLoading(false);
        }
    };

    const fetchPurchases = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available. Please login again.');
            setLoading(false);
            return;
        }

        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'No shop selected. Please select a shop first.');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            // Send both username and shop_id to backend - shop_id is now the primary filter
            const response = await fetch('https://ourzestapp.online/oz_backend/purchases.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                // Data is filtered by backend based on shop_id
                setPurchases(data.data);

                // Update shop info from response
                if (data.shop_id) {
                    setShopInfo({
                        shop_id: data.shop_id,
                        shop_name: data.shop_name || '',
                        shop_code: data.shop_code || ''
                    });
                }

                // Show shop info in toast
                if (data.shop_name) {
                    showToast('info', `${translations.willShowFromShop || 'Showing purchases from shop'}: ${data.shop_name}`);
                }

                if (data.data.length === 0) {
                    showToast('info', translations.noUserRecords || 'No purchases found in this shop');
                }
            } else {
                if (data.message.includes('not assigned to any shop') || data.message.includes('no permission')) {
                    showToast('error', translations.noShopPermission || 'You do not have permission to view purchases in this shop');
                } else {
                    showToast('error', data.message || translations.networkError || 'Network error occurred');
                }
            }
        } catch (error) {
            console.error('Error fetching purchases:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...purchases];

        // Apply quick search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(purchase =>
                purchase.bill_no?.toLowerCase().includes(term) ||
                purchase.supplier_name?.toLowerCase().includes(term) ||
                purchase.phone?.toLowerCase().includes(term) ||
                purchase.supplierid?.toString().includes(term) ||
                purchase.username?.toLowerCase().includes(term)
            );
        }

        // Apply payment status filter
        if (filterType === 'paid') {
            filtered = filtered.filter(purchase => {
                const balance = parseFloat(purchase.balance) || 0;
                return balance === 0;
            });
        } else if (filterType === 'unpaid') {
            filtered = filtered.filter(purchase => {
                const paid = parseFloat(purchase.paid) || 0;
                return paid === 0;
            });
        } else if (filterType === 'partiallyPaid') {
            filtered = filtered.filter(purchase => {
                const paid = parseFloat(purchase.paid) || 0;
                const grandTotal = parseFloat(purchase.grand_total) || 0;
                const balance = parseFloat(purchase.balance) || 0;
                return paid > 0 && balance > 0;
            });
        }

        // Apply advanced filters based on filterBy
        if (filterBy === 'billNo' && billNoFilter.trim()) {
            const billNoTerm = billNoFilter.toLowerCase();
            filtered = filtered.filter(purchase =>
                purchase.bill_no?.toLowerCase().includes(billNoTerm)
            );
        }

        if (filterBy === 'supplier' && supplierFilter.trim()) {
            const supplierTerm = supplierFilter.toLowerCase();
            filtered = filtered.filter(purchase =>
                purchase.supplier_name?.toLowerCase().includes(supplierTerm)
            );
        }

        if (filterBy === 'phone' && phoneFilter.trim()) {
            const phoneTerm = phoneFilter.toLowerCase();
            filtered = filtered.filter(purchase =>
                purchase.phone?.toLowerCase().includes(phoneTerm)
            );
        }

        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const addedByTerm = addedByFilter.toLowerCase();
            filtered = filtered.filter(purchase =>
                purchase.username?.toLowerCase().includes(addedByTerm)
            );
        }

        // Apply date range filter
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(purchase => {
                if (!purchase.date) return false;
                const purchaseDate = new Date(purchase.date);
                purchaseDate.setHours(0, 0, 0, 0);
                return purchaseDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(purchase => {
                if (!purchase.date) return false;
                const purchaseDate = new Date(purchase.date);
                return purchaseDate <= end;
            });
        }

        setFilteredPurchases(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setBillNoFilter('');
        setSupplierFilter('');
        setPhoneFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setFilterType('all');
    };

    const handleSearch = () => {
        applyFilters();
    };

    const handleView = async (purchase) => {
        setSelectedPurchase(purchase);
        
        // If items are not included, fetch them
        if (!purchase.items && purchase.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/purchases.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: purchase.id,
                        username: user?.username,
                        shop_id: currentShop.shop_id
                    })
                });

                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    setSelectedPurchase(data.data);
                }
            } catch (error) {
                console.error('Error fetching purchase details:', error);
            }
        }
        
        setShowViewModal(true);
    };

    const handleEdit = (purchase) => {
        // Check if purchase belongs to current shop
        if (purchase.shop_id !== currentShop?.shop_id) {
            showToast('error', 'You can only edit purchases from the current shop');
            return;
        }

        setSelectedPurchase(purchase);
        
        // Format date for input
        const formattedDate = purchase.date ? purchase.date.split('T')[0] : '';
        
        setEditFormData({
            id: purchase.id,
            date: formattedDate,
            bill_no: purchase.bill_no || '',
            supplierid: purchase.supplierid || '',
            supplier_name: purchase.supplier_name || '',
            phone: purchase.phone || '',
            address: purchase.address || '',
            grand_total: purchase.grand_total || '',
            paid: purchase.paid || '',
            balance: purchase.balance || '',
            items: purchase.items || []
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (purchaseId) => {
        // Find the purchase and check if it belongs to current shop
        const purchase = purchases.find(p => p.id === purchaseId);
        if (!purchase || purchase.shop_id !== currentShop?.shop_id) {
            showToast('error', 'You can only delete purchases from the current shop');
            return;
        }

        setPurchaseToDelete(purchaseId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!purchaseToDelete || !currentShop) return;

        // Additional security check
        const purchase = purchases.find(p => p.id === purchaseToDelete);
        if (!purchase || purchase.shop_id !== currentShop.shop_id) {
            showToast('error', 'You can only delete purchases from the current shop');
            setShowDeleteModal(false);
            setPurchaseToDelete(null);
            return;
        }

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/purchases.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete',
                    id: purchaseToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Purchase deleted successfully');
                fetchPurchases(); // Refresh the list
                setShowDeleteModal(false);
                setPurchaseToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting purchase');
            }
        } catch (error) {
            console.error('Error deleting purchase:', error);
            showToast('error', translations.deleteError || 'Error deleting purchase');
        }
    };

    // Validate edit form
    const validateEditForm = () => {
        const newErrors = {};

        if (!editFormData.date) {
            newErrors.date = translations.dateRequired || 'Date is required';
        }

        if (!editFormData.bill_no.trim()) {
            newErrors.bill_no = translations.billNoRequired || 'Bill number is required';
        }

        if (!editFormData.supplier_name.trim()) {
            newErrors.supplier_name = translations.supplierNameRequired || 'Supplier name is required';
        }

        if (!editFormData.grand_total || parseFloat(editFormData.grand_total) <= 0) {
            newErrors.grand_total = translations.grandTotalRequired || 'Grand total is required';
        }

        if (editFormData.paid === '' || editFormData.paid === null) {
            newErrors.paid = translations.paidRequired || 'Paid amount is required';
        }

        if (editFormData.paid && editFormData.grand_total && parseFloat(editFormData.paid) > parseFloat(editFormData.grand_total)) {
            newErrors.paid = 'Paid amount cannot exceed grand total';
        }

        setEditFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        
        let newValue = value;
        let updatedFormData = { ...editFormData, [name]: newValue };

        // Recalculate balance if grand_total or paid changes
        if (name === 'grand_total' || name === 'paid') {
            const grandTotal = parseFloat(name === 'grand_total' ? newValue : editFormData.grand_total) || 0;
            const paid = parseFloat(name === 'paid' ? newValue : editFormData.paid) || 0;
            updatedFormData.balance = (grandTotal - paid).toFixed(2);
        }

        setEditFormData(updatedFormData);

        // Clear error when user starts typing
        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdatePurchase = async (e) => {
        e.preventDefault();

        if (!selectedPurchase || !currentShop) return;

        // Check if purchase belongs to current shop
        if (selectedPurchase.shop_id !== currentShop.shop_id) {
            showToast('error', 'You can only update purchases from the current shop');
            return;
        }

        // Validate form
        if (!validateEditForm()) {
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/purchases.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'edit',
                    id: selectedPurchase.id,
                    date: editFormData.date,
                    bill_no: editFormData.bill_no,
                    supplierid: editFormData.supplierid,
                    supplier_name: editFormData.supplier_name,
                    phone: editFormData.phone,
                    address: editFormData.address,
                    grand_total: editFormData.grand_total,
                    paid: editFormData.paid,
                    balance: editFormData.balance,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.purchaseUpdated || 'Purchase updated successfully');
                fetchPurchases(); // Refresh the list
                setShowEditModal(false);
                setSelectedPurchase(null);
                setEditFormErrors({});
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating purchase');
            }
        } catch (error) {
            console.error('Error updating purchase:', error);
            showToast('error', translations.updateError || 'Error updating purchase');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Helper function to process logo for PDF
    const getLogoForPDF = () => {
        if (!businessInfo.bussinesslogo) return '';

        try {
            if (businessInfo.bussinesslogo.startsWith('data:image')) {
                return businessInfo.bussinesslogo;
            }

            if (businessInfo.bussinesslogo.startsWith('http')) {
                return businessInfo.bussinesslogo;
            }

            if (businessInfo.bussinesslogo.length > 100) {
                return `data:image/png;base64,${businessInfo.bussinesslogo}`;
            }

            return '';
        } catch (error) {
            console.error('Error processing logo:', error);
            return '';
        }
    };

    // Export to PDF
    const handleExportPDF = async () => {
        if (filteredPurchases.length === 0) {
            showToast('error', translations.noPurchasesFound || 'No data to export!');
            return;
        }

        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'No shop selected');
            return;
        }

        try {
            const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
            const primaryColor = currentTheme || '#10b981';
            const primaryLight = hexToRgba(primaryColor, 0.1);
            const primaryLighter = hexToRgba(primaryColor, 0.03);
            const primaryVeryLight = hexToRgba(primaryColor, 0.015);

            // Helper function to generate lighter color
            function hexToRgba(hex, alpha = 1) {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            }

            // Format date globally (YYYY-MM-DD)
            const formatDateGlobal = (dateString) => {
                const date = new Date(dateString);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Get current date in global format
            const currentDateGlobal = () => {
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Get current time in 24-hour format
            const currentTimeGlobal = () => {
                const now = new Date();
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                return `${hours}:${minutes}`;
            };

            // CSS for proper RTL text rendering
            const rtlTextStyles = isRTL ? `
                unicode-bidi: plaintext;
                text-rendering: optimizeLegibility;
                font-kerning: normal;
                font-variant-ligatures: common-ligatures;
                font-feature-settings: "calt", "liga", "kern";
                letter-spacing: 0;
                word-spacing: normal;
            ` : '';

            // Common header cell styles with RTL text fixes
            const headerCellStyle = `
                padding: 12px 8px; 
                border: none; 
                text-align: center; 
                font-weight: 600; 
                font-size: 10px; 
                letter-spacing: ${isRTL ? '0' : '0.3px'};
                border-right: 1px solid rgba(255,255,255,0.1);
                ${rtlTextStyles}
            `;

            // Create PDF in landscape mode for better table fit
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Add professional pattern background
            const addPatternBackground = (pdfDoc) => {
                pdfDoc.setFillColor(250, 251, 252);
                pdfDoc.rect(0, 0, pageWidth, pageHeight, 'F');
                
                const themeColor = hexToRgba(primaryColor, 0.5).match(/\d+/g);
                const r = parseInt(themeColor[0]);
                const g = parseInt(themeColor[1]);
                const b = parseInt(themeColor[2]);
                
                pdfDoc.setFillColor(r, g, b);
                pdfDoc.setDrawColor(r, g, b);
                pdfDoc.setLineWidth(0.2);
                
                const dotSize = 0.3;
                const dotSpacing = 8;
                
                for (let x = dotSpacing; x < pageWidth - dotSpacing; x += dotSpacing) {
                    for (let y = dotSpacing; y < pageHeight - dotSpacing; y += dotSpacing) {
                        pdfDoc.circle(x, y, dotSize, 'F');
                    }
                }
                
                pdfDoc.setFillColor(255, 255, 255);
                pdfDoc.setTextColor(0, 0, 0);
            };

            // Calculate how many rows can fit on a page
            const rowsPerPage = 20;
            const totalRows = filteredPurchases.length;
            const totalPages = Math.ceil(totalRows / rowsPerPage);

            // Generate header HTML with shop info
            const headerHTML = `
                <!-- Header Section - Centered -->
                <div style="margin-bottom: 3px; padding-bottom: 5px; text-align: center; border-bottom: 2px solid ${primaryVeryLight}">
                    <!-- Title Section - Centered -->
                    <div style="margin-bottom: 3px;">
                        <h1 style="color: ${primaryColor}; font-size: 24px; margin: 0 0 5px 0; font-weight: 800; letter-spacing: ${isRTL ? 'normal' : '-0.5px'}; ${rtlTextStyles}">
                            ${businessInfo.businessname || currentShop?.shop_name || (translations.purchaseList || 'Purchase List')}
                        </h1>
                        <div style="color: #666; font-size: 12px; font-weight: 500; margin-top: 3px; ${rtlTextStyles}">
                            ${translations.purchaseReport || 'Purchase Report'} - ${currentDateGlobal()} ${currentTimeGlobal()}
                            ${currentShop ? ` | ${translations.shopInfo || 'Shop'}: ${currentShop.shop_name}` : ''}
                        </div>
                    </div>
                    
                    <!-- Summary Stats -->
                    <div style="display: flex; justify-content: center; gap: 15px; margin-top: 10px; flex-wrap: wrap;">
                        <div style="background: ${primaryVeryLight}; padding: 5px 12px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                            <div style="font-size: 10px; color: #666; ${rtlTextStyles}">${translations.totalPurchases || 'Total Purchases'}</div>
                            <div style="font-size: 15px; font-weight: 700; color: ${primaryColor};">${totalPurchases}</div>
                        </div>
                        <div style="background: ${primaryVeryLight}; padding: 5px 12px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                            <div style="font-size: 10px; color: #666; ${rtlTextStyles}">${translations.totalAmount || 'Total Amount'}</div>
                            <div style="font-size: 15px; font-weight: 700; color: #059669;">Rs. ${totalGrandTotal.toFixed(2)}</div>
                        </div>
                        <div style="background: ${primaryVeryLight}; padding: 5px 12px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                            <div style="font-size: 10px; color: #666; ${rtlTextStyles}">${translations.totalPaid || 'Total Paid'}</div>
                            <div style="font-size: 15px; font-weight: 700; color: #3b82f6;">Rs. ${totalPaid.toFixed(2)}</div>
                        </div>
                        <div style="background: #fef3c7; padding: 5px 12px; border-radius: 20px; border: 1px solid rgba(245, 158, 11, 0.2);">
                            <div style="font-size: 10px; color: #92400e; ${rtlTextStyles}">${translations.totalBalance || 'Total Balance'}</div>
                            <div style="font-size: 15px; font-weight: 700; color: #d97706;">Rs. ${totalBalance.toFixed(2)}</div>
                        </div>
                        <div style="background: #fee2e2; padding: 5px 12px; border-radius: 20px; border: 1px solid rgba(239, 68, 68, 0.2);">
                            <div style="font-size: 10px; color: #991b1b; ${rtlTextStyles}">${translations.unpaidFilter || 'Unpaid'}</div>
                            <div style="font-size: 15px; font-weight: 700; color: #dc2626;">${unpaidCount}</div>
                        </div>
                    </div>
                </div>
            `;

            // Generate table header HTML
            const tableHeaderHTML = `
                <table style="width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 5px; font-size: 8px; border-radius: 5px; overflow: hidden; border: 1px solid ${hexToRgba(primaryColor, 0.15)};">
                    <thead>
                        <tr style="background: linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.9)}); color: white;">
                            <th style="${headerCellStyle} width: 30px;">
                                ${translations.serialNo || 'No.'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.date || 'Date'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.billNo || 'Bill No'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.supplierName || 'Supplier'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.phone || 'Phone'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.items || 'Items'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.grandTotal || 'Total'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.paid || 'Paid'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.balance || 'Balance'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.paymentStatus || 'Status'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.addedBy || 'Added By'}
                            </th>
                            <th style="${headerCellStyle}">
                                ${translations.shopInfo || 'Shop'}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            // Generate each page
            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, totalRows);
                const pagePurchases = filteredPurchases.slice(startRow, endRow);

                // Generate table rows for this page
                const tableRowsHTML = pagePurchases.map((purchase, index) => {
                    const grandTotal = parseFloat(purchase.grand_total) || 0;
                    const paid = parseFloat(purchase.paid) || 0;
                    const balance = parseFloat(purchase.balance) || 0;
                    const itemCount = purchase.items ? purchase.items.length : '—';
                    
                    let statusClass = '';
                    let statusText = '';
                    if (balance === 0) {
                        statusClass = 'paid';
                        statusText = translations.paid || 'Paid';
                    } else if (paid === 0) {
                        statusClass = 'unpaid';
                        statusText = translations.unpaid || 'Unpaid';
                    } else {
                        statusClass = 'partial';
                        statusText = translations.partiallyPaid || 'Partial';
                    }
                    
                    return `
                        <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : 'background-color: #ffffff;'}">
                            <td style="padding: 6px 3px; border: none; text-align: center; font-family: 'Courier New', monospace; font-weight: 600; color: ${primaryColor}; border-bottom: 1px solid #f0f0f0;">
                                <div style="display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; background: ${primaryLight}; border-radius: 3px; font-size: 8px;">
                                    ${startRow + index + 1}
                                </div>
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 500; color: #374151; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${formatDateGlobal(purchase.date)}
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 600; color: #1f2937; border-bottom: 1px solid #f0f0f0; font-size: 8px; font-family: 'SF Mono', 'Courier New', monospace;">
                                ${purchase.bill_no || '—'}
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 500; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px; ${rtlTextStyles}">
                                <div style="font-weight: 600; color: #1f2937;">${purchase.supplier_name || '—'}</div>
                                <div style="font-size: 7px; color: #6b7280; margin-top: 2px;">ID: ${purchase.supplierid || '—'}</div>
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 500; color: #374151; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${purchase.phone || '—'}
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 600; color: #7c3aed; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-flex; align-items: center; justify-content: center; padding: 2px 6px; background: #f3e8ff; border-radius: 12px; border: 1px solid #ddd6fe;">
                                    ${itemCount}
                                </div>
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 600; color: #059669; border-bottom: 1px solid #f0f0f0; font-size: 8px; font-family: 'SF Mono', 'Courier New', monospace;">
                                Rs. ${grandTotal.toFixed(2)}
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 600; color: #3b82f6; border-bottom: 1px solid #f0f0f0; font-size: 8px; font-family: 'SF Mono', 'Courier New', monospace;">
                                Rs. ${paid.toFixed(2)}
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 600; border-bottom: 1px solid #f0f0f0; font-size: 8px; font-family: 'SF Mono', 'Courier New', monospace;">
                                <div style="display: inline-flex; align-items: center; justify-content: center; padding: 2px 8px; border-radius: 12px; ${balance > 0 ? 'background: #fef3c7; color: #d97706;' : 'background: #d1fae5; color: #059669;'}">
                                    Rs. ${balance.toFixed(2)}
                                </div>
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 500; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-flex; align-items: center; justify-content: center; padding: 2px 8px; border-radius: 12px; 
                                    ${statusClass === 'paid' ? 'background: #d1fae5; color: #059669;' : 
                                      statusClass === 'unpaid' ? 'background: #fee2e2; color: #dc2626;' : 
                                      'background: #fef3c7; color: #d97706;'}">
                                    ${statusText}
                                </div>
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 500; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-flex; align-items: center; gap: 2px; padding: 2px 6px; background: #e0e7ff; border-radius: 12px; color: #4f46e5;">
                                    <span>👤</span>
                                    <span>${purchase.username || '—'}</span>
                                </div>
                            </td>
                            <td style="padding: 6px 3px; border: none; text-align: center; font-weight: 500; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-flex; align-items: center; gap: 2px; padding: 2px 6px; background: #f3e8ff; border-radius: 12px; color: #7c3aed;">
                                    <span>🏪</span>
                                    <span>${purchase.shop_name || currentShop?.shop_name || '—'}</span>
                                </div>
                            </td>
                        </tr>
                    `;
                }).join('');

                // Complete table HTML for this page
                const tableHTML = tableHeaderHTML + tableRowsHTML + '</tbody></table>';

                // Complete page HTML for this page
                const pageHTML = `
                    <div style="width: 1200px; padding: 12px 20px; background-color: rgba(255, 255, 255, 0.98); font-family: ${isRTL ? "'Noto Naskh Arabic', 'Segoe UI', Tahoma" : "'Inter', 'Segoe UI', sans-serif"}; direction: ${isRTL ? 'rtl' : 'ltr'}; text-align: ${isRTL ? 'right' : 'left'}; color: #1a1a1a; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.03); border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        ${headerHTML}
                        <div style="overflow-x: auto;">
                            ${tableHTML}
                        </div>
                        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 8px; color: #6b7280;">
                            ${translations.generatedBy || 'Generated by'}: ${businessInfo.fullname || user?.username || ''} | ${translations.purchaseReport || 'Purchase Report'} | Page ${pageNum} of ${totalPages}
                        </div>
                    </div>
                `;

                // Create container for this page
                const pdfContainer = document.createElement('div');
                pdfContainer.style.position = 'absolute';
                pdfContainer.style.left = '-9999px';
                pdfContainer.style.top = '0';
                pdfContainer.style.width = '1200px';
                pdfContainer.innerHTML = pageHTML;
                document.body.appendChild(pdfContainer);

                // Convert to canvas with optimized settings
                const canvas = await html2canvas(pdfContainer, {
                    scale: 1.5,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false,
                    onclone: (clonedDoc) => {
                        if (isRTL) {
                            const elements = clonedDoc.querySelectorAll('div, span, td, th, h1, td > div');
                            elements.forEach(el => {
                                el.style.unicodeBidi = 'plaintext';
                                el.style.textRendering = 'optimizeLegibility';
                                el.style.fontFeatureSettings = '"calt", "liga", "kern"';
                                el.style.letterSpacing = '0';
                            });
                        }
                    }
                });

                // Remove container immediately after capturing
                document.body.removeChild(pdfContainer);

                // Calculate image dimensions to fit page
                const imgWidth = pageWidth - 25;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                // Add new page if not the first page
                if (pageNum > 1) {
                    pdf.addPage();
                }

                // Add professional pattern background
                addPatternBackground(pdf);

                // Add content to PDF
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 12.5, 12.5, imgWidth, Math.min(imgHeight, pageHeight - 25));

                // Add centered page number overlay
                pdf.setFontSize(9);
                pdf.setTextColor(120, 120, 120);
                pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            }

            // Generate filename with shop info
            const fileName = isRTL
                ? `${currentShop?.shop_name ? currentShop.shop_name.replace(/\s+/g, '_') + '_' : ''}${translations.purchaseList || 'Purchase_List'}_${currentDateGlobal()}.pdf`
                : `${currentShop?.shop_name ? currentShop.shop_name.replace(/\s+/g, '_') + '_' : ''}${translations.purchaseList || 'Purchase_List'}_${currentDateGlobal()}.pdf`;

            pdf.save(fileName);

            showToast('success', `PDF exported successfully with ${filteredPurchases.length} purchase records across ${totalPages} pages! Total Amount: Rs. ${totalGrandTotal.toFixed(2)}`);

        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', 'Error generating PDF. Please try again.');
        }
    };

    // Format date in global standard format (YYYY-MM-DD)
    const formatDateGlobal = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '-';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    // Get payment status
    const getPaymentStatus = (paid, grandTotal) => {
        const paidAmount = parseFloat(paid) || 0;
        const totalAmount = parseFloat(grandTotal) || 0;
        const balance = totalAmount - paidAmount;
        
        if (balance <= 0) {
            return { status: 'paid', text: translations.paid || 'Paid', color: '#10b981' };
        } else if (paidAmount === 0) {
            return { status: 'unpaid', text: translations.unpaid || 'Unpaid', color: '#ef4444' };
        } else {
            return { status: 'partial', text: translations.partiallyPaid || 'Partial', color: '#f59e0b' };
        }
    };

    // Get today's date in YYYY-MM-DD format for date inputs
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Set max date for end date based on start date
    useEffect(() => {
        if (startDate && endDateRef.current) {
            endDateRef.current.min = startDate;
        }
    }, [startDate]);

    // Set max date for start date to today
    useEffect(() => {
        const today = getTodayDate();
        if (startDateRef.current) {
            startDateRef.current.max = today;
        }
        if (endDateRef.current) {
            endDateRef.current.max = today;
        }
    }, []);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button 
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }

        return (
            <div className="shop-indicator-modern active">
                <div className="shop-badge">
                    <FontAwesomeIcon icon={faStore} />
                    <div className="shop-details">
                        <span className="shop-name">{currentShop.shop_name}</span>
                        <span className="shop-code">({currentShop.shop_code || currentShop.shop_id})</span>
                    </div>
                </div>
                <div className="shop-info-note">
                    {translations.willShowFromShop || 'Showing purchases from this shop'}
                </div>
                <button 
                    className="btn-modern btn-switch-shop"
                    onClick={() => setShowShopSelector(true)}
                    type="button"
                >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span>{translations.switchShop || 'Switch Shop'}</span>
                </button>
            </div>
        );
    };

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faShoppingCart} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.purchaseList || 'Purchase List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopInfo || 'Shop'}: ${currentShop.shop_name}`
                                    : `${translations.allPurchases || 'Shop Purchases'} - ${user?.username}`}
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Summary Stats */}
            <div className="summary-stats-modern">
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                        <FontAwesomeIcon icon={faShoppingCart} style={{ color: '#10b981' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totalPurchases}</div>
                        <div className="stat-label">{translations.totalPurchases || 'Total Purchases'}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                        <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#3b82f6' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalGrandTotal)}</div>
                        <div className="stat-label">{translations.totalAmount || 'Total Amount'}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                        <FontAwesomeIcon icon={faCreditCard} style={{ color: '#f59e0b' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalPaid)}</div>
                        <div className="stat-label">{translations.totalPaid || 'Total Paid'}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ef4444' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalBalance)}</div>
                        <div className="stat-label">{translations.totalBalance || 'Total Balance'}</div>
                    </div>
                </div>
            </div>

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="billNo">{translations.billNoFilter || 'Bill Number'}</option>
                            <option value="supplier">{translations.supplierFilter || 'Supplier Name'}</option>
                            <option value="phone">{translations.phone || 'Phone'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                        </select>
                    </div>

                    {filterBy === 'billNo' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={billNoFilter}
                                onChange={(e) => setBillNoFilter(e.target.value)}
                                placeholder={translations.billNoFilter || 'Bill number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'supplier' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={supplierFilter}
                                onChange={(e) => setSupplierFilter(e.target.value)}
                                placeholder={translations.supplierFilter || 'Supplier name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'phone' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={phoneFilter}
                                onChange={(e) => setPhoneFilter(e.target.value)}
                                placeholder={translations.phone || 'Phone number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder={translations.addedByFilter || 'Added by username...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={startDateRef}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.startDate || 'Start Date'}
                                />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={endDateRef}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.endDate || 'End Date'}
                                />
                            </div>
                        </>
                    )}

                    <div className="filter-group compact">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.paymentStatus || 'Payment Status'}</option>
                            <option value="paid">{translations.paidFilter || 'Paid'}</option>
                            <option value="unpaid">{translations.unpaidFilter || 'Unpaid'}</option>
                            <option value="partiallyPaid">{translations.partiallyPaidFilter || 'Partially Paid'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            {/* <FontAwesomeIcon icon={faSearch} className="search-icon" /> */}
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search purchases in shop...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={fetchPurchases}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={handleExportPDF}
                            className="export-btn compact"
                            disabled={loading || filteredPurchases.length === 0 || !currentShop}
                            title={translations.exportToPDF || 'Export PDF'}
                        >
                            <FontAwesomeIcon icon={faFilePdf} />
                            <span className="btn-text">{translations.exportToPDF || 'PDF'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading shop purchase data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view purchases'}</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.date || 'Date'}</th>
                                            <th className="centered-cell">{translations.billNo || 'Bill No'}</th>
                                            <th className="centered-cell">{translations.supplierName || 'Supplier'}</th>
                                            <th className="centered-cell">{translations.phone || 'Phone'}</th>
                                            <th className="centered-cell">{translations.items || 'Items'}</th>
                                            <th className="centered-cell">{translations.grandTotal || 'Total'}</th>
                                            <th className="centered-cell">{translations.paid || 'Paid'}</th>
                                            <th className="centered-cell">{translations.balance || 'Balance'}</th>
                                            <th className="centered-cell">{translations.paymentStatus || 'Status'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="centered-cell">{translations.shopInfo || 'Shop'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentPurchases.map((purchase, index) => {
                                            const paymentStatus = getPaymentStatus(purchase.paid, purchase.grand_total);
                                            const itemCount = purchase.items ? purchase.items.length : '—';
                                            const addedBy = purchase.username || '-';
                                            const shopName = purchase.shop_name || currentShop?.shop_name || '-';
                                            const isCurrentShop = purchase.shop_id === currentShop?.shop_id;
                                            
                                            return (
                                                <tr key={purchase.id}>
                                                    <td className="id-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faHashtag} className="cell-icon" />
                                                            <span className="cell-text">{startIndex + index + 1}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faCalendar} className="cell-icon" />
                                                            <span className="cell-text">{formatDateGlobal(purchase.date)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faFileInvoice} className="cell-icon" />
                                                            <span className="cell-text" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{purchase.bill_no}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faBuilding} className="cell-icon" />
                                                            <span className="cell-text">{purchase.supplier_name}</span>
                                                            <div className="cell-subtext">
                                                                <FontAwesomeIcon icon={faTag} size="xs" />
                                                                <span>ID: {purchase.supplierid || '-'}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faPhone} className="cell-icon" />
                                                            <span className="cell-text">{purchase.phone || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="items-badge">
                                                                <FontAwesomeIcon icon={faBoxes} />
                                                                <span>{itemCount}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="grand-total" style={{ color: '#059669', fontWeight: 'bold' }}>
                                                                {formatCurrency(purchase.grand_total)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="paid-amount" style={{ color: '#3b82f6', fontWeight: 'bold' }}>
                                                                {formatCurrency(purchase.paid)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`balance-amount ${parseFloat(purchase.balance) > 0 ? 'has-balance' : 'no-balance'}`} 
                                                                  style={{ 
                                                                      color: parseFloat(purchase.balance) > 0 ? '#d97706' : '#059669',
                                                                      fontWeight: 'bold'
                                                                  }}>
                                                                {formatCurrency(purchase.balance)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <div 
                                                                className="payment-status-badge"
                                                                style={{ 
                                                                    backgroundColor: paymentStatus.color + '20',
                                                                    color: paymentStatus.color,
                                                                    border: `1px solid ${paymentStatus.color}40`
                                                                }}
                                                            >
                                                                {paymentStatus.text}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`added-by-badge ${addedBy === user?.username ? 'self' : ''}`}>
                                                                <FontAwesomeIcon icon={faUser} className="cell-icon" size="xs" />
                                                                <span className="cell-text">{addedBy}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`shop-badge-mini ${isCurrentShop ? 'current-shop' : ''}`}>
                                                                <FontAwesomeIcon icon={faStore} className="cell-icon" size="xs" />
                                                                <span className="cell-text">{shopName}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="cell-content centered-content actions-content">
                                                            <button
                                                                onClick={() => handleView(purchase)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title={translations.view || 'View'}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(purchase)}
                                                                className={`action-btn-modern edit-btn-modern ${!isCurrentShop ? 'disabled' : ''}`}
                                                                title={translations.edit || 'Edit'}
                                                                disabled={!isCurrentShop}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(purchase.id)}
                                                                className={`action-btn-modern delete-btn-modern ${!isCurrentShop ? 'disabled' : ''}`}
                                                                title={translations.delete || 'Delete'}
                                                                disabled={!isCurrentShop}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* No Data Message */}
                                {currentPurchases.length === 0 && !loading && currentShop && (
                                    <div className="no-data-modern">
                                        <FontAwesomeIcon icon={faShoppingCart} />
                                        <p>{user ? (translations.noUserRecords || 'No purchases found in this shop') : (translations.noPurchasesFound || 'No purchases found')}</p>
                                        {user && currentShop && (
                                            <button
                                                className="btn-modern btn-primary-modern"
                                                style={{ marginTop: '20px' }}
                                                onClick={() => window.location.href = '/AddPurchase'}
                                            >
                                                <FontAwesomeIcon icon={faPlus} />
                                                <span>Add Your First Purchase</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredPurchases.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredPurchases.length)} {translations.of || 'of'} {filteredPurchases.length} {translations.purchases || 'purchases'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopInfo || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                                <span style={{ marginLeft: '20px', color: '#059669', fontWeight: '600' }}>
                                    {translations.totalAmount || 'Total Amount'}: {formatCurrency(totalGrandTotal)}
                                </span>
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedPurchase && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faShoppingCart} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.purchaseDetails || 'Purchase Details'}</h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="detail-section">
                                <h4 className="section-title">{translations.purchaseInfo || 'Purchase Information'}</h4>
                                <div className="details-grid">
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.purchaseId || 'Purchase ID'}:</span>
                                        <span className="detail-value">#{selectedPurchase.id}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.date || 'Date'}:</span>
                                        <span className="detail-value">{formatDateGlobal(selectedPurchase.date)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.billNo || 'Bill Number'}:</span>
                                        <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{selectedPurchase.bill_no}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.supplierName || 'Supplier Name'}:</span>
                                        <span className="detail-value">{selectedPurchase.supplier_name}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.supplierid || 'Supplier ID'}:</span>
                                        <span className="detail-value">{selectedPurchase.supplierid || '-'}</span>
                                    </div>
                                    {selectedPurchase.phone && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.phone || 'Phone'}:</span>
                                            <span className="detail-value">{selectedPurchase.phone}</span>
                                        </div>
                                    )}
                                    {selectedPurchase.address && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.address || 'Address'}:</span>
                                            <span className="detail-value">{selectedPurchase.address}</span>
                                        </div>
                                    )}
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.grandTotal || 'Grand Total'}:</span>
                                        <span className="detail-value" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedPurchase.grand_total)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paid || 'Paid'}:</span>
                                        <span className="detail-value" style={{ color: '#3b82f6', fontWeight: 'bold' }}>{formatCurrency(selectedPurchase.paid)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.balance || 'Balance'}:</span>
                                        <span className="detail-value" style={{ color: parseFloat(selectedPurchase.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}>
                                            {formatCurrency(selectedPurchase.balance)}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paymentStatus || 'Payment Status'}:</span>
                                        <span className="detail-value">
                                            <div className="payment-status-badge" style={{ 
                                                backgroundColor: getPaymentStatus(selectedPurchase.paid, selectedPurchase.grand_total).color + '20',
                                                color: getPaymentStatus(selectedPurchase.paid, selectedPurchase.grand_total).color,
                                                border: `1px solid ${getPaymentStatus(selectedPurchase.paid, selectedPurchase.grand_total).color}40`,
                                                display: 'inline-block'
                                            }}>
                                                {getPaymentStatus(selectedPurchase.paid, selectedPurchase.grand_total).text}
                                            </div>
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.addedBy || 'Added By'}:</span>
                                        <span className="detail-value">
                                            <span className="added-by-badge" style={{ background: '#e0e7ff', color: '#4f46e5' }}>
                                                <FontAwesomeIcon icon={faUser} size="xs" />
                                                {selectedPurchase.username || '-'}
                                            </span>
                                        </span>
                                    </div>
                                    {selectedPurchase.shop_name && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.shopInfo || 'Shop'}:</span>
                                            <span className="detail-value">
                                                <span className="shop-badge-mini current-shop">
                                                    <FontAwesomeIcon icon={faStore} size="xs" />
                                                    {selectedPurchase.shop_name} {selectedPurchase.shop_code ? `(${selectedPurchase.shop_code})` : ''}
                                                </span>
                                            </span>
                                        </div>
                                    )}
                                    {selectedPurchase.bill_file && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.billFile || 'Bill File'}:</span>
                                            <span className="detail-value">
                                                <a href={`https://ourzestapp.online/oz_backend/${selectedPurchase.bill_file}`} target="_blank" rel="noopener noreferrer" className="file-link">
                                                    <FontAwesomeIcon icon={faFileImage} />
                                                    {translations.viewBill || 'View Bill'}
                                                </a>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Items Section */}
                            {selectedPurchase.items && selectedPurchase.items.length > 0 && (
                                <div className="detail-section">
                                    <h4 className="section-title">{translations.items || 'Purchase Items'}</h4>
                                    <div className="items-table-container">
                                        <table className="items-table">
                                            <thead>
                                                <tr>
                                                    <th>{translations.serialNo || '#'}</th>
                                                    <th>{translations.itemProduct || 'Product'}</th>
                                                    <th>{translations.itemBatch || 'Batch'}</th>
                                                    <th>{translations.itemUnit || 'Unit'}</th>
                                                    <th>{translations.itemPrice || 'Price'}</th>
                                                    <th>{translations.itemQuantity || 'Qty'}</th>
                                                    <th>{translations.itemTotal || 'Total'}</th>
                                                    <th>{translations.itemExpiry || 'Expiry'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedPurchase.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{item.product_name}</td>
                                                        <td className="batch-cell">{item.batch_no}</td>
                                                        <td>{translations[item.unit] || item.unit}</td>
                                                        <td className="amount-cell">{formatCurrency(item.price)}</td>
                                                        <td className="amount-cell">{parseFloat(item.quantity).toFixed(2)}</td>
                                                        <td className="amount-cell total">{formatCurrency(item.total)}</td>
                                                        <td>{item.expiry_date ? formatDateGlobal(item.expiry_date) : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="6" className="text-right">{translations.grandTotal || 'Grand Total'}:</td>
                                                    <td className="amount-cell grand-total">{formatCurrency(selectedPurchase.grand_total)}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                {translations.close || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedPurchase && (
                <div className="modal-overlay-modern edit-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content-modern edit-modal-content" onClick={(e) => e.stopPropagation()}>
                        {/* Edit Form Header */}
                        <div className="form-header-modern edit-form-header">
                            <div className="header-content">
                                <div className="header-icon">
                                    <FontAwesomeIcon icon={faEdit} />
                                </div>
                                <div className="header-text">
                                    <h1>{translations.editPurchase || 'Edit Purchase'}</h1>
                                    <p>{translations.fillDetails || 'Fill in the purchase details below'}</p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleUpdatePurchase} className="modern-form edit-form">
                            <div className="form-grid-modern edit-form-grid">
                                {/* Date Field */}
                                <div className={`form-group-modern ${editFormErrors.date ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                                        <input
                                            type="date"
                                            id="date"
                                            name="date"
                                            value={editFormData.date}
                                            onChange={handleEditFormChange}
                                            required
                                            max={getTodayDate()}
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="date" className="input-label">
                                            {translations.date || 'Date'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.date && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.date}
                                        </div>
                                    )}
                                </div>

                                {/* Bill No Field */}
                                <div className={`form-group-modern ${editFormErrors.bill_no ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                        <input
                                            type="text"
                                            id="bill_no"
                                            name="bill_no"
                                            value={editFormData.bill_no}
                                            onChange={handleEditFormChange}
                                            required
                                            placeholder={translations.billNo || 'Enter bill number'}
                                            disabled={editLoading || true} // Disabled in edit mode
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                            className="readonly-input"
                                        />
                                        <label htmlFor="bill_no" className="input-label">
                                            {translations.billNo || 'Bill Number'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.bill_no && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.bill_no}
                                        </div>
                                    )}
                                </div>

                                {/* Supplier Name Field */}
                                <div className={`form-group-modern ${editFormErrors.supplier_name ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                                        <input
                                            type="text"
                                            id="supplier_name"
                                            name="supplier_name"
                                            value={editFormData.supplier_name}
                                            onChange={handleEditFormChange}
                                            required
                                            placeholder={translations.supplierName || 'Enter supplier name'}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label htmlFor="supplier_name" className="input-label">
                                            {translations.supplierName || 'Supplier Name'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.supplier_name && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.supplier_name}
                                        </div>
                                    )}
                                </div>

                                {/* Phone Field */}
                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                        <input
                                            type="text"
                                            id="phone"
                                            name="phone"
                                            value={editFormData.phone}
                                            onChange={handleEditFormChange}
                                            placeholder={translations.phone || 'Enter phone number'}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label htmlFor="phone" className="input-label">
                                            {translations.phone || 'Phone'}
                                            <span className="optional"> {translations.optional || '(Optional)'}</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Address Field */}
                                <div className="form-group-modern full-width">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                                        <textarea
                                            id="address"
                                            name="address"
                                            value={editFormData.address}
                                            onChange={handleEditFormChange}
                                            placeholder={translations.address || 'Enter address'}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                            rows="2"
                                        />
                                        <label htmlFor="address" className="input-label">
                                            {translations.address || 'Address'}
                                            <span className="optional"> {translations.optional || '(Optional)'}</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Grand Total Field */}
                                <div className={`form-group-modern ${editFormErrors.grand_total ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faDollarSign} className="input-icon" />
                                        <input
                                            type="number"
                                            id="grand_total"
                                            name="grand_total"
                                            value={editFormData.grand_total}
                                            onChange={handleEditFormChange}
                                            required
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="grand_total" className="input-label">
                                            {translations.grandTotal || 'Grand Total'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.grand_total && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.grand_total}
                                        </div>
                                    )}
                                </div>

                                {/* Paid Amount Field */}
                                <div className={`form-group-modern ${editFormErrors.paid ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                        <input
                                            type="number"
                                            id="paid"
                                            name="paid"
                                            value={editFormData.paid}
                                            onChange={handleEditFormChange}
                                            required
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="paid" className="input-label">
                                            {translations.paid || 'Paid Amount'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.paid && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.paid}
                                        </div>
                                    )}
                                </div>

                                {/* Balance Display */}
                                <div className="form-group-modern">
                                    <div className="calculation-display">
                                        <div className="calculation-item">
                                            <span className="calculation-label">
                                                <FontAwesomeIcon icon={faCalculator} />
                                                {translations.balance || 'Balance'}:
                                            </span>
                                            <span className={`calculation-value ${parseFloat(editFormData.balance) > 0 ? 'has-balance' : 'no-balance'}`}
                                                  style={{ color: parseFloat(editFormData.balance) > 0 ? '#d97706' : '#059669' }}>
                                                Rs. {parseFloat(editFormData.balance || 0).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="form-actions-modern edit-form-actions">
                                <button
                                    type="button"
                                    className="btn-modern btn-secondary-modern"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={editLoading}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    <span>{translations.cancel || 'Cancel'}</span>
                                </button>
                                <button
                                    type="submit"
                                    className="btn-modern btn-primary-modern"
                                    disabled={editLoading}
                                >
                                    {editLoading ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            <span>{translations.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>{translations.saveChanges || 'Save Changes'}</span>
                                            <FontAwesomeIcon icon={faSave} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        {/* Purchase Info */}
                        <div className="edit-product-info">
                            <p><strong>{translations.purchaseId || 'Purchase ID'}:</strong> #{selectedPurchase.id}</p>
                            <p><strong>{translations.billNo || 'Bill No'}:</strong> {selectedPurchase.bill_no}</p>
                            <p><strong>{translations.addedBy || 'Added By'}:</strong> {selectedPurchase.username}</p>
                            <p><strong>{translations.shopInfo || 'Shop'}:</strong> {selectedPurchase.shop_name || currentShop?.shop_name}</p>
                            <p className="readonly-notice">
                                <FontAwesomeIcon icon={faInfoCircle} />
                                {translations.readonlyNotice || 'Bill number and items cannot be edited after creation'}
                            </p>
                        </div>

                        {/* Loading Overlay for Edit */}
                        {editLoading && (
                            <div className="loading-overlay edit-loading-overlay">
                                <div className="loading-content">
                                    <div className="loading-spinner"></div>
                                    <p>{translations.updating || 'Updating...'}</p>
                                    <p className="loading-subtext">Please wait while we update purchase data</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--customer-error-color)' }}>
                                    {translations.confirmDelete || 'Confirm Delete'}
                                </h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this purchase?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This action cannot be undone. All associated items will also be deleted.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseList;